// Admin page: global defaults + the role/permission model.
//
// Only the ping-marker timings are live global settings today; everything else
// is the access contract that server-side accounts will enforce later. The page
// is visible so roles can be previewed, but editing/saving requires the Admin
// preview role and the server admin token.

import {
  ROLE_INFO,
  canRole,
  loadAdminToken,
  normalizeAdmin,
  type AdminSettings,
  type Role,
} from "../admin";
import { dsElement, refreshComponentStyles } from "./daisy";

export interface AdminPanelCallbacks {
  getSettings: () => AdminSettings;
  /** Apply a changed value locally (live preview). */
  onApply: (settings: AdminSettings) => void;
  /** PUT the global defaults; the server checks the admin token. */
  onSave: (settings: AdminSettings, token: string) => Promise<{ ok: boolean; error?: string }>;
  getRole: () => Role;
  onRole: (role: Role) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

export class AdminPanel {
  private settings: AdminSettings;
  private role: Role;

  constructor(
    private readonly host: HTMLElement,
    private readonly cb: AdminPanelCallbacks,
  ) {
    this.settings = normalizeAdmin(cb.getSettings());
    this.role = cb.getRole();
    this.render();
  }

  refresh(): void {
    this.settings = normalizeAdmin(this.cb.getSettings());
    this.role = this.cb.getRole();
    this.render();
  }

  private card(title: string): { card: HTMLElement; body: HTMLElement } {
    const card = el("section", "settings-card settings-card--wide");
    card.dataset.ds = "card";
    card.dataset.dsKeep = "settings-card settings-card--wide";
    const body = el("div", "card-body");
    body.append(el("h3", "card-title", title));
    card.append(body);
    return { card, body };
  }

  private render(): void {
    this.host.innerHTML = "";
    const wrap = el("div", "settings-cards");
    const isAdmin = canRole(this.role, "admin");

    const intro = this.card("Admin");
    intro.body.append(
      el(
        "div",
        "weapon-note",
        "Global defaults live on the server and are applied to every client. " +
          "Saving needs the admin token (the same token as /design publishing). " +
          "Roles below are a local preview until user accounts land.",
      ),
    );
    const roleRow = el("div", "rule-row");
    roleRow.append(el("label", "", "Preview role"));
    const roleSel = el("select");
    for (const r of ROLE_INFO) {
      const o = el("option", "", r.label);
      o.value = r.id;
      roleSel.append(o);
    }
    roleSel.value = this.role;
    roleSel.addEventListener("change", () => {
      this.role = roleSel.value as Role;
      this.cb.onRole(this.role);
      this.render();
    });
    roleRow.append(roleSel, el("span", "rule-sync", isAdmin ? "full access" : "read-only"));
    intro.body.append(roleRow);
    wrap.append(intro.card);

    // ---- Settings defaults editor ------------------------------------------
    const defaults = this.card("Settings defaults");
    defaults.body.append(
      el(
        "div",
        "weapon-note",
        "Search and edit every client/UI default, reset values to shipped, and " +
          "set per-setting visibility (none / player / tester / admin). The editor " +
          "requires the server admin token.",
      ),
    );
    const open = el("a", "btn btn-outline btn-sm", "Open settings defaults editor");
    open.href = "admin/settings/";
    open.target = "_blank";
    open.rel = "noopener";
    defaults.body.append(open);
    if (!isAdmin) {
      defaults.body.append(
        el("div", "weapon-note", "Switch the preview role to Admin to use the editor."),
      );
    }
    wrap.append(defaults.card);

    // ---- Ping marker timings ------------------------------------------------
    const ping = this.card("Ping / tag");
    ping.body.append(
      el(
        "div",
        "weapon-note",
        "There is one active ping. A new ping fades the old one out, then the " +
          "new marker stays until its lifetime ends.",
      ),
    );
    ping.body.append(
      this.sliderRow("Active ping lifetime", "pingTtlMs", 500, 30000, 100, "s"),
      this.sliderRow("Old ping fade (replace)", "pingFadeMs", 0, 3000, 50, "s"),
      this.sliderRow("Active ping end fade", "pingEndFadeMs", 0, 3000, 50, "s"),
      this.sliderRow("Ping cooldown (spam guard)", "pingCooldownMs", 0, 2000, 50, "s"),
    );
    if (!isAdmin) {
      ping.body.append(el("div", "weapon-note", "Admin role required to edit these defaults."));
    }
    wrap.append(ping.card);

    // ---- Save globally ------------------------------------------------------
    const save = this.card("Publish global defaults");
    const tokenRow = el("div", "rule-row");
    tokenRow.append(el("label", "", "Admin token"));
    const token = el("input");
    token.type = "password";
    token.placeholder = "server admin token";
    token.value = loadAdminToken();
    token.autocomplete = "off";
    dsElement(token, "input", { size: "sm", keep: "w-full" });
    tokenRow.append(token);
    const status = el("span", "rule-sync", "");
    save.body.append(tokenRow, status);
    const saveBtn = el("button", "xhair-btn", "Save globally");
    dsElement(saveBtn, "button", { style: "outline", size: "sm" });
    saveBtn.disabled = !isAdmin;
    saveBtn.addEventListener("click", () => {
      status.textContent = "saving…";
      status.className = "rule-sync";
      void this.cb.onSave(this.settings, token.value.trim()).then((r) => {
        status.textContent = r.ok ? "saved · global" : (r.error ?? "save failed");
        status.className = "rule-sync " + (r.ok ? "ok" : "warn");
        if (r.ok) {
          this.settings = normalizeAdmin(this.settings);
          this.cb.onApply(this.settings);
        }
      });
    });
    save.body.append(saveBtn);
    save.body.append(
      el(
        "div",
        "weapon-note",
        "GET /api/admin is public; PUT requires the admin token. The file lives on the backend data volume.",
      ),
    );
    wrap.append(save.card);

    // ---- Roles / capabilities ----------------------------------------------
    const roles = this.card("Roles & access");
    roles.body.append(
      el(
        "div",
        "weapon-note",
        "Four levels. This is the contract the server will enforce once accounts exist; " +
          "the preview selector above only changes this browser.",
      ),
    );
    for (const info of ROLE_INFO) {
      const row = el("div", "admin-role");
      const head = el("div", "admin-role-head");
      head.append(el("strong", "", info.label));
      if (info.id === this.role) head.append(el("span", "admin-role-you", "preview"));
      row.append(head, el("div", "weapon-note", info.summary));
      const caps = el("div", "admin-role-caps");
      for (const c of info.capabilities) caps.append(el("span", "admin-cap", c));
      row.append(caps);
      roles.body.append(row);
    }
    wrap.append(roles.card);

    // ---- Match controls (planned) ------------------------------------------
    const match = this.card("Match controls (planned)");
    match.body.append(
      el(
        "div",
        "weapon-note",
        "Player: votes/timeout/ready. Moderator: kick, mute, pause, restart round, map, balance. " +
          "Admin: everything including global defaults and accounts. Enforcement comes with accounts.",
      ),
    );
    const planned = ["Timeout / ready", "Kick / mute", "Pause / restart round", "Change map", "Balance teams"];
    const chips = el("div", "admin-role-caps");
    for (const p of planned) chips.append(el("span", "admin-cap admin-cap-disabled", p));
    match.body.append(chips);
    wrap.append(match.card);

    this.host.append(wrap);
    refreshComponentStyles();
  }

  private sliderRow(
    label: string,
    key: "pingTtlMs" | "pingFadeMs" | "pingEndFadeMs" | "pingCooldownMs",
    min: number,
    max: number,
    step: number,
    unit: "s",
  ): HTMLElement {
    const row = el("div", "rule-row");
    row.append(el("label", "", label));
    const range = el("input");
    range.type = "range";
    range.min = String(min);
    range.max = String(max);
    range.step = String(step);
    range.value = String(this.settings[key]);
    dsElement(range, "range");
    const out = el("span", "rule-sync", `${(this.settings[key] / 1000).toFixed(1)} ${unit}`);
    range.addEventListener("input", () => {
      const v = Number(range.value);
      if (!Number.isFinite(v)) return;
      this.settings[key] = Math.min(max, Math.max(min, Math.round(v)));
      out.textContent = `${(this.settings[key] / 1000).toFixed(1)} ${unit}`;
      this.cb.onApply(normalizeAdmin(this.settings));
    });
    range.disabled = !canRole(this.role, "admin");
    row.append(range, out);
    return row;
  }
}
